import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  email: string;
  organization: string | null;
}

interface AuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  hasSubscription: boolean;
  checkSubscriptionStatus: () => Promise<boolean>;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  checkAdminStatus: (userId: string) => Promise<boolean>;
  checkApprovalStatus: (email: string) => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      isAdmin: false,
      user: null,
      loading: false,
      error: null,
      hasSubscription: false,

      checkSession: async () => {
        try {
          console.log('Checking session...');
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('Session error:', sessionError);
            set({ isAuthenticated: false, isAdmin: false, user: null, hasSubscription: false });
            return;
          }
          
          if (!session?.user) {
            console.log('No active session found');
            set({ isAuthenticated: false, isAdmin: false, user: null, hasSubscription: false });
            return;
          }

          console.log('Session found for user:', session.user.email);

          // Get user profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('organization')
            .eq('id', session.user.id)
            .maybeSingle();

          // Check if user has admin role
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('*')
            .eq('user_id', session.user.id)
            .eq('role', 'admin');

          const isAdmin = roleData && roleData.length > 0;
          
          console.log('Admin check:', { isAdmin });

          // Check subscription status
          const hasSubscription = await get().checkSubscriptionStatus();

          set({
            isAuthenticated: true,
            isAdmin,
            user: {
              id: session.user.id,
              email: session.user.email!,
              organization: profile?.organization || null,
            },
            error: null,
            hasSubscription
          });
        } catch (err) {
          console.error('Session check error:', err);
          set({ 
            isAuthenticated: false, 
            isAdmin: false, 
            user: null,
            error: null,
            hasSubscription: false
          });
        }
      },

      checkAdminStatus: async (userId: string) => {
        try {
          const { data: roleData, error: roleError } = await supabase
            .from('user_roles')
            .select('*')
            .eq('user_id', userId)
            .eq('role', 'admin');

          if (roleError) {
            console.error('Admin check error:', roleError);
            return false;
          }

          return roleData && roleData.length > 0;
        } catch (err) {
          console.error('Admin check error:', err);
          return false;
        }
      },

      checkApprovalStatus: async (email: string) => {
        try {
          // 管理者アカウントは常に承認済み
          if (email === 'admin@example.com' || email === 'info@jrpo.or.jp' || email === 'analysis.jrpo@gmail.com') {
            return true;
          }

          // 管理者ロールを持つユーザーは常に承認済み
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            const isAdmin = await get().checkAdminStatus(session.user.id);
            if (isAdmin) {
              return true;
            }

            // Stripeのサブスクリプションステータスをチェック
            // アクティブなサブスクリプションがある場合は自動的に承認済みとする
            const { data: subscriptionData, error: subError } = await supabase
              .from('stripe_user_subscriptions')
              .select('subscription_status')
              .eq('user_id', session.user.id)
              .maybeSingle();

            if (!subError && subscriptionData && 
                (subscriptionData.subscription_status === 'active' || 
                 subscriptionData.subscription_status === 'trialing')) {
              console.log('User has active subscription, auto-approving');
              return true;
            }
          }

          // 承認ステータスを確認（フォールバック）
          try {
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-approval-status`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
              },
              body: JSON.stringify({ email })
            });
            
            if (!response.ok) {
              console.error('Error checking approval status:', await response.text());
              // Edge Functionが失敗した場合、デフォルトで承認済みとする（Stripe決済後の場合）
              return true;
            }
            
            const data = await response.json();
            return data.approved;
          } catch (err) {
            console.error('Error checking approval status:', err);
            // ネットワークエラーの場合も、デフォルトで承認済みとする
            return true;
          }
        } catch (err) {
          console.error('Approval check error:', err);
          return true;
        }
      },

      checkSubscriptionStatus: async () => {
        try {
          // サブスクリプション情報を取得
          const { data, error } = await supabase
            .from('stripe_user_subscriptions')
            .select('subscription_status')
            .maybeSingle();
          
          if (error) {
            console.error('Error checking subscription status:', error);
            return false;
          }
          
          // アクティブなサブスクリプションがあるか、トライアル期間中か確認
          const hasActiveSubscription = data && 
            (data.subscription_status === 'active' || 
             data.subscription_status === 'trialing');
          
          set({ hasSubscription: hasActiveSubscription });
          return hasActiveSubscription;
        } catch (err) {
          console.error('Subscription check error:', err);
          set({ hasSubscription: false });
          return false;
        }
      },

      login: async (email: string, password: string) => {
        try {
          set({ loading: true, error: null });
          
          // デバッグ用：入力値を詳細にログ出力
          console.log('Login attempt details:', {
            originalEmail: email,
            emailLength: email.length,
            trimmedEmail: email.trim(),
            trimmedEmailLength: email.trim().length,
            lowercaseEmail: email.toLowerCase().trim(),
            passwordLength: password.length,
            trimmedPasswordLength: password.trim().length,
            // 特殊文字の確認
            hasSpaces: email.includes(' '),
            hasNewlines: email.includes('\n') || email.includes('\r'),
            hasFullWidthChars: /[^\x00-\x7F]/.test(email),
          });

          // より厳密なクリーニング
          const cleanEmail = email
            .toLowerCase()
            .trim()
            .replace(/[\s\u3000]+/g, '') // 全角スペースも削除
            .replace(/[\r\n]+/g, ''); // 改行を削除
          
          const cleanPassword = password
            .trim()
            .replace(/[\r\n]+/g, ''); // 改行を削除

          console.log('Cleaned credentials:', {
            cleanEmail,
            cleanEmailLength: cleanEmail.length,
          });

          const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
            email: cleanEmail,
            password: cleanPassword
          });

          if (signInError) throw signInError;
          if (!authData.user) throw new Error('ログインに失敗しました');

          console.log('Login successful for:', authData.user.email);

          // Get user profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('organization')
            .eq('id', authData.user.id)
            .maybeSingle();

          // Check if user has admin role
          const isAdmin = await get().checkAdminStatus(authData.user.id);
          
          console.log('Admin check:', { isAdmin });

          // Check subscription status
          const hasSubscription = await get().checkSubscriptionStatus();

          // 承認チェックを行う（Stripeサブスクリプションがある場合は自動承認）
          const isApproved = await get().checkApprovalStatus(email);
          if (!isApproved && !hasSubscription) {
            // サブスクリプションがない場合のみ承認チェックでブロック
            set({ 
              error: 'このアカウントはまだ承認されていません。管理者の承認をお待ちください。',
              isAuthenticated: false,
              user: null,
              isAdmin: false,
              loading: false,
              hasSubscription: false
            });
            await supabase.auth.signOut();
            return false;
          }

          set({
            isAuthenticated: true,
            isAdmin,
            user: {
              id: authData.user.id,
              email: authData.user.email!,
              organization: profile?.organization || null,
            },
            error: null,
            hasSubscription
          });

          return true;
        } catch (err) {
          console.error('Login error:', err);
          let errorMessage = 'ログインに失敗しました';
          
          if (err instanceof Error) {
            if (err.message.includes('Invalid login credentials')) {
              errorMessage = 'メールアドレスまたはパスワードが正しくありません';
            } else if (err.message.includes('Too many requests')) {
              errorMessage = 'ログイン試行回数が多すぎます。しばらく時間をおいて再度お試しください';
            }
          }
          
          set({ 
            error: errorMessage,
            isAuthenticated: false,
            user: null,
            isAdmin: false,
            hasSubscription: false
          });
          return false;
        } finally {
          set({ loading: false });
        }
      },

      logout: async () => {
        try {
          console.log('Logging out...');
          
          // Clear local state first
          set({
            isAuthenticated: false,
            isAdmin: false,
            user: null,
            error: null,
            hasSubscription: false
          });

          // Clear persisted state from localStorage
          localStorage.removeItem('sprint-analysis-auth');
          
          // Check if there's a valid session before attempting to sign out
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.user) {
            try {
              await supabase.auth.signOut();
            } catch (signOutError) {
              console.error('Sign out error:', signOutError);
              // Continue since local state is already cleared
            }
          } else {
            console.log('No valid session found, skipping Supabase signOut');
          }
        } catch (err) {
          console.error('Logout error:', err);
          // Ensure local state is cleared even if there's an error
          set({
            isAuthenticated: false,
            isAdmin: false,
            user: null,
            error: null,
            hasSubscription: false
          });
          localStorage.removeItem('sprint-analysis-auth');
        }
      },
    }),
    {
      name: 'sprint-analysis-auth',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          try {
            return JSON.parse(str);
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        isAdmin: state.isAdmin,
        hasSubscription: state.hasSubscription,
      }),
    }
  )
);